<template>
    <aside class="ei-bridge" :style="eiDeltaColorCssVars">
        <div v-if="!enIntIdx" class="ei-bridge-empty">
            <i class="fas fa-columns ei-bridge-empty-icon"></i>
            <span>목록에서 항목을 선택하면<br />요약이 표시됩니다</span>
        </div>

        <template v-else>
            <div v-if="masterLoadError" class="ei-bridge-error">{{ masterLoadError }}</div>
            <div v-else-if="bridgeChartsCalcListLoading" class="ei-bridge-loading">
                <i class="fas fa-spinner fa-spin"></i> 불러오는 중…
            </div>
            <template v-else-if="error">{{ error }}</template>
            <template v-else>
                <div class="ei-bridge-head">
                    <div class="ei-bridge-identity">
                        <div class="ei-bridge-icon-wrap" :class="'ei-bridge-icon-wrap--' + categoryKey">
                            <i v-if="isAreaCategory" class="icon-display fas fa-building"></i>
                            <i v-else class="fas" :class="categoryIcon"></i>
                        </div>
                        <div class="ei-bridge-titles">
                            <span class="ei-bridge-code">{{ master.enIntCode }}</span>
                            <span class="ei-bridge-name">{{ master.enIntName }}</span>
                            <span class="ei-bridge-cat">{{ categoryDisplayName }}</span>
                        </div>
                    </div>
                    <!-- <div class="ei-bridge-unit-line">
                        <span class="ei-bridge-unit-key">원단위</span>
                        <span class="ei-bridge-unit-val">{{ master.intensityUnit || "—" }}</span>
                    </div> -->
                </div>

                <div class="ei-bridge-scroll">
                    <!-- 1차: 최근 원단위 -->
                    <div class="ei-bridge-primary">
                        <div class="ei-bridge-primary-meta">
                            <span class="ei-bridge-primary-label">{{ primaryIntensityLabel }}</span>
                            <span v-if="primaryMonthRow" class="ei-bridge-status-chip" :class="latestStatusCls">
                                {{ latestStatusLabel }}
                            </span>
                        </div>
                        <div class="ei-bridge-primary-val-row">
                            <span class="ei-bridge-primary-num">{{ latestCalcValFmt }}</span>
                            <span class="ei-bridge-primary-unit">{{ master.intensityUnit || "" }}</span>
                        </div>
                        <div class="ei-bridge-primary-month">
                            {{ primaryMonthCaption }}
                        </div>
                    </div>

                    <!-- 2차: 기간 평균 + 트렌드 -->
                    <div class="ei-bridge-stats-row">
                        <div class="ei-bridge-stat">
                            <span class="ei-bridge-stat-label">기간 평균</span>
                            <span class="ei-bridge-stat-num">{{ meanCalcValFmt }}</span>
                            <span class="ei-bridge-stat-sub">{{ master.intensityUnit || "" }}</span>
                        </div>
                        <div class="ei-bridge-stat-sep"></div>
                        <div class="ei-bridge-stat">
                            <span class="ei-bridge-stat-label">{{ trendRowLabel }}</span>
                            <span class="ei-bridge-stat-num" :class="trendCls">{{ trendFmt }}</span>
                            <span class="ei-bridge-stat-sub">{{ trendDesc }}</span>
                        </div>
                    </div>

                    <div class="ei-bridge-compare">
                        <div class="ei-bridge-compare-head">
                            <span class="ei-bridge-compare-title">{{ compareInsightTitle }}</span>
                            <span class="ei-bridge-compare-base">{{ compareInsightSubtitle }}</span>
                        </div>
                        <div v-if="isMonthPeriod" class="ei-bridge-compare-grid">
                            <div class="ei-bridge-compare-card">
                                <div class="ei-bridge-compare-card-head">
                                    <span class="ei-bridge-compare-card-label">전월</span>
                                    <span class="ei-bridge-compare-card-range">{{ momPrevYmLabel }}</span>
                                </div>
                                <div class="ei-bridge-compare-card-body">
                                    <span class="ei-bridge-compare-card-val">{{ momPrevCalcValFmt }}</span>
                                    <span class="ei-bridge-compare-diff" :class="momMonthRateCls">{{
                                        momMonthRateFmt
                                    }}</span>
                                </div>
                            </div>
                            <div class="ei-bridge-compare-card">
                                <div class="ei-bridge-compare-card-head">
                                    <span class="ei-bridge-compare-card-label">전년 동월</span>
                                    <span class="ei-bridge-compare-card-range">{{ yoyYmLabel }}</span>
                                </div>
                                <div class="ei-bridge-compare-card-body">
                                    <span class="ei-bridge-compare-card-val">{{ yoyPeerCalcValFmt }}</span>
                                    <span class="ei-bridge-compare-diff" :class="yoyMonthRateCls">{{
                                        yoyMonthRateFmt
                                    }}</span>
                                </div>
                            </div>
                        </div>
                        <div v-else class="ei-bridge-compare-grid">
                            <div class="ei-bridge-compare-card">
                                <div class="ei-bridge-compare-card-head">
                                    <span class="ei-bridge-compare-card-label">직전 연도</span>
                                    <span class="ei-bridge-compare-card-range">{{ momPeriodLabel }}</span>
                                </div>
                                <div class="ei-bridge-compare-card-body">
                                    <span class="ei-bridge-compare-card-val">{{ momMeanCalcValFmt }}</span>
                                    <span class="ei-bridge-compare-diff" :class="momMeanDiffCls">{{
                                        momMeanDiffFmt
                                    }}</span>
                                </div>
                            </div>
                            <div class="ei-bridge-compare-card">
                                <div class="ei-bridge-compare-card-head">
                                    <span class="ei-bridge-compare-card-label">전전 연도</span>
                                    <span class="ei-bridge-compare-card-range">{{ yoyPeriodLabel }}</span>
                                </div>
                                <div class="ei-bridge-compare-card-body">
                                    <span class="ei-bridge-compare-card-val">{{ yoyMeanCalcValFmt }}</span>
                                    <span class="ei-bridge-compare-diff" :class="yoyMeanDiffCls">{{
                                        yoyMeanDiffFmt
                                    }}</span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="ei-bridge-charts-panel" :aria-busy="bridgeChartsCalcListLoading ? 'true' : 'false'">
                        <div
                            v-if="bridgeChartsCalcListLoading"
                            class="ei-bridge-charts-panel-overlay"
                            aria-hidden="true"
                        >
                            <span class="ei-bridge-charts-panel-spinner">
                                <i class="fas fa-spinner fa-spin"></i>
                                <span>불러오는 중…</span>
                            </span>
                        </div>
                        <div
                            class="ei-bridge-charts-panel-body"
                            :class="{ 'ei-bridge-charts-panel-body--blocked': bridgeChartsCalcListLoading }"
                        >
                            <div class="ei-bridge-section">
                                <div class="ei-bridge-section-title">
                                    <span>{{ chartYearLabel }} 월별 원단위</span>
                                    <div class="ei-bridge-chart-legend" aria-label="차트 범례">
                                        <span class="ei-bridge-legend-item">
                                            <span
                                                class="ei-bridge-legend-swatch ei-bridge-legend-swatch--bar"
                                                aria-hidden="true"
                                            ></span>
                                            <span class="ei-bridge-legend-text ei-bridge-legend-text--bar">기준</span>
                                        </span>
                                        <span class="ei-bridge-legend-sep" aria-hidden="true">·</span>
                                        <span class="ei-bridge-legend-item">
                                            <span
                                                class="ei-bridge-legend-swatch ei-bridge-legend-swatch--mom"
                                                aria-hidden="true"
                                            ></span>
                                            <span class="ei-bridge-legend-text ei-bridge-legend-text--mom">전월</span>
                                        </span>
                                        <span class="ei-bridge-legend-sep" aria-hidden="true">·</span>
                                        <span class="ei-bridge-legend-item">
                                            <span
                                                class="ei-bridge-legend-swatch ei-bridge-legend-swatch--yoy"
                                                aria-hidden="true"
                                            ></span>
                                            <span class="ei-bridge-legend-text ei-bridge-legend-text--yoy"
                                                >전년 동월</span
                                            >
                                        </span>
                                        <span
                                            v-if="meanCalcValNumber !== null"
                                            class="ei-bridge-legend-sep"
                                            aria-hidden="true"
                                            >·</span
                                        >
                                        <span v-if="meanCalcValNumber !== null" class="ei-bridge-legend-item">
                                            <span
                                                class="ei-bridge-legend-swatch ei-bridge-legend-swatch--avg"
                                                aria-hidden="true"
                                            ></span>
                                            <span class="ei-bridge-legend-text ei-bridge-legend-text--avg">평균</span>
                                        </span>
                                    </div>
                                </div>
                                <EnIntBridgeBarChart
                                    :key="chartRenderKey"
                                    :get-master-summary-api-state="getMasterSummaryApiState"
                                    :get-monthly-series-api-state="getMonthlySeriesApiState"
                                    :intensity-unit="master.intensityUnit || ''"
                                    @bar-hover="onBarHover"
                                    @bar-hover-end="onBarHoverEnd"
                                />
                            </div>

                            <div v-if="hasBoxplotData" class="ei-bridge-section ei-bridge-section--boxplot">
                                <div class="ei-bridge-section-title">원단위 값 분포</div>
                                <div class="ei-bridge-boxplot-grow">
                                    <EnIntBridgeBoxPlot
                                        :key="chartRenderKey + '-box'"
                                        :get-master-summary-api-state="getMasterSummaryApiState"
                                        :get-monthly-series-api-state="getMonthlySeriesApiState"
                                        :intensity-unit="master.intensityUnit || ''"
                                        :highlight-y="barHoverHighlightY"
                                        :highlight-label="barHoverHighlightLabel"
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div class="ei-bridge-actions">
                    <button type="button" class="ei-bridge-edit-btn" @click="$emit('edit', enIntIdx)">
                        <i class="fas fa-pen"></i> 수정
                    </button>
                    <div class="ei-bridge-meta-dates" aria-label="등록·수정 일시">
                        <div class="ei-bridge-meta-col">
                            <span class="ei-bridge-meta-k">생성</span>
                            <span class="ei-bridge-meta-v">{{ masterRegDisplay }}</span>
                        </div>
                        <div class="ei-bridge-meta-col ei-bridge-meta-col--end">
                            <span class="ei-bridge-meta-k">수정</span>
                            <span class="ei-bridge-meta-v">{{ masterModDisplay }}</span>
                        </div>
                    </div>
                </div>
            </template>
        </template>
    </aside>
</template>

<script>
import backEndApi from "@src/api/backEndApi";
import EnIntBridgeBarChart from "./EnIntBridgeBarChart.vue";
import EnIntBridgeBoxPlot from "./EnIntBridgeBoxPlot.vue";
import {
    mapEnIntCategoryState,
    categoryNameByValue,
    formatYm,
    categoryIconClass,
    calcStatusBadge,
    yearFromDatePeriod,
    isValidDatePeriod,
    calcListBoundsFromDatePeriod,
    bridgeChartMonthsFromCalcRows,
    ENINT_DATE_TYPE_MONTH,
    EI_DELTA_COLOR_CSS_VARS,
} from "../utils/enIntUi.js";

/** 마스터 regDate/modDate 등 — 초 생략, 빈 값은 — */
function formatEnIntMasterMetaDt(raw) {
    if (raw == null || raw === "") return "—";
    const s = String(raw).trim().replace("T", " ");
    if (!s) return "—";
    const sp = s.indexOf(" ");
    if (sp === -1) return s;
    const datePart = s.slice(0, sp);
    const timePart = s.slice(sp + 1);
    const hm = timePart.split(":").slice(0, 2).join(":");
    return hm ? `${datePart} ${hm}` : datePart;
}

export default {
    name: "EnIntBridgePanel",
    components: { EnIntBridgeBarChart, EnIntBridgeBoxPlot },
    props: {
        enIntIdx: { type: Number, default: null },
        reloadToken: { type: Number, default: 0 },
        dateType: { type: String, required: true },
        dateValue: { type: Number, required: true },
        /** 상위(EnIntMasterMain)에서 단일 getMaster로 조회한 마스터 */
        masterApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
        /** 상위에서 단일 getCalcList(union 구간)로 조회한 본문 */
        sharedCalcListApiState: {
            type: Object,
            default: () => ({ response: null, data: null, loading: false, error: "" }),
        },
    },
    computed: {
        sharedCalcListLoading() {
            return Boolean(this.sharedCalcListApiState?.loading);
        },
        /** enIntIdx + calcList 기준 구간 — DataInput의 lastStableFetchMeta와 동일 규칙 */
        bridgeFetchMetaKey() {
            if (!this.enIntIdx || !isValidDatePeriod(this.dateType, this.dateValue)) return null;
            const bounds = calcListBoundsFromDatePeriod(this.dateType, this.dateValue);
            if (!bounds) return null;
            return `${this.enIntIdx}|${bounds.fromYm}|${bounds.toYm}`;
        },
        /** 바·박스 차트용: 기준 연·월 구간만 잘라 월별 시리즈 형태로 맞춤 */
        getMonthlySeriesApiState() {
            const empty = { response: null, data: null, loading: false, error: "" };
            if (!this.enIntIdx || !isValidDatePeriod(this.dateType, this.dateValue)) return empty;
            const s = this.sharedCalcListApiState;
            if (!s) return empty;
            const bounds = calcListBoundsFromDatePeriod(this.dateType, this.dateValue);
            if (!bounds) return empty;
            const meta = `${this.enIntIdx}|${bounds.fromYm}|${bounds.toYm}`;
            const allRows = Array.isArray(s.data?.rows) ? s.data.rows : [];
            const baseRows = allRows.filter((r) => {
                const ym = Number(r.regYm);
                return Number.isFinite(ym) && ym >= bounds.fromYm && ym <= bounds.toYm;
            });
            const ok = s.response?.status === 200;
            let months = ok ? bridgeChartMonthsFromCalcRows(baseRows) : [];
            const staleCalc = s.loading && this.lastStableFetchMeta != null && meta === this.lastStableFetchMeta;
            if (staleCalc && Array.isArray(this.cachedBridgeMonths)) {
                months = this.cachedBridgeMonths;
            }
            const chartData = months.length ? { months } : null;
            const errStale = staleCalc
                ? ""
                : s.loading
                  ? ""
                  : s.error || (ok ? "" : "월별 데이터를 불러오지 못했습니다.");
            return {
                response: s.response,
                data: chartData,
                loading: Boolean(s.loading),
                error: errStale,
            };
        },
        /**
         * 차트 영역 오버레이: 상위 getCalcList(sharedCalcList)가 진행 중일 때만.
         * 마스터·getMasterSummary 로딩과 무관. 동일 bridgeFetchMeta 재조회 시에는 stale-while-revalidate로 숨김.
         */
        bridgeChartsCalcListLoading() {
            if (!this.sharedCalcListLoading) return false;
            const mk = this.bridgeFetchMetaKey;
            if (mk != null && mk === this.lastStableFetchMeta) {
                return false;
            }
            return true;
        },
        master() {
            return this.masterApiState?.data || {};
        },
        masterLoading() {
            return Boolean(this.masterApiState?.loading);
        },
        masterLoadError() {
            return this.masterApiState?.error || "";
        },
        summary() {
            return this.getMasterSummaryApiState?.data || null;
        },
        hasBoxplotData() {
            const months = this.getMonthlySeriesApiState?.data?.months;
            if (!Array.isArray(months)) return false;
            const values = months.filter((m) => Number.isFinite(Number(m?.value)));
            return values.length >= 2;
        },
        ...mapEnIntCategoryState(),
        chartYearLabel() {
            const y = yearFromDatePeriod(this.dateType, this.dateValue);
            return y != null ? String(y) : "—";
        },
        chartRenderKey() {
            return `${this.enIntIdx || 0}-${this.reloadToken}-${this.dateType}-${this.dateValue}`;
        },
        bridgeLoadKey() {
            return `${this.enIntIdx ?? ""}|${this.reloadToken}|${this.dateType}|${this.dateValue}`;
        },
        categoryKey() {
            return String(this.master.enIntCategory || "x").toLowerCase();
        },
        isAreaCategory() {
            return String(this.master.enIntCategory || "").toUpperCase() === "AREA";
        },
        categoryIcon() {
            return categoryIconClass(this.master.enIntCategory);
        },
        categoryDisplayName() {
            return (
                categoryNameByValue(this.enIntCategoryItems, this.master.enIntCategory) ||
                this.master.enIntCategory ||
                "—"
            );
        },
        barHoverHighlightY() {
            const m = this.barHoverMeta;
            return m && Number.isFinite(m.value) ? m.value : null;
        },
        barHoverHighlightLabel() {
            const m = this.barHoverMeta;
            return m && m.category ? String(m.category) : "";
        },
        masterRegDisplay() {
            return formatEnIntMasterMetaDt(this.master.regDate);
        },
        masterModDisplay() {
            return formatEnIntMasterMetaDt(this.master.modDate);
        },
        isMonthPeriod() {
            return String(this.dateType || "").toUpperCase() === ENINT_DATE_TYPE_MONTH;
        },
        /** 월간: focusMonth, 연간: latestMonth */
        primaryMonthRow() {
            if (this.isMonthPeriod && this.summary?.focusMonth) return this.summary.focusMonth;
            return this.summary?.latestMonth || null;
        },
        primaryIntensityLabel() {
            if (this.isMonthPeriod) return " 원단위";
            return "연 평균";
        },
        primaryMonthCaption() {
            const m = this.primaryMonthRow;
            return m?.regYm != null ? `${formatYm(m.regYm)} 기준` : "—";
        },
        trendRowLabel() {
            return this.isMonthPeriod ? "조회 월 대비 기간 평균" : "기준 연도 최근 월 대비 연 평균";
        },
        compareInsightTitle() {
            return this.isMonthPeriod ? "비교 데이터 (선택한 월)" : "비교 데이터 (연평균)";
        },
        compareInsightSubtitle() {
            if (this.isMonthPeriod) {
                const fy = this.summary?.focusMonth?.regYm ?? this.summary?.focusYm;
                if (fy != null) return `${formatYm(fy)} 기준`;
                return "—";
            }
            return this.basePeriodLabel;
        },
        momPrevYmLabel() {
            const ym = this.summary?.mom?.prevYm;
            return ym != null ? formatYm(ym) : "—";
        },
        yoyYmLabel() {
            const ym = this.summary?.yoy?.yoyYm;
            return ym != null ? formatYm(ym) : "—";
        },
        momPrevCalcValFmt() {
            return this.formatCompareIntensityValue(this.summary?.mom?.prevCalcVal);
        },
        yoyPeerCalcValFmt() {
            return this.formatCompareIntensityValue(this.summary?.yoy?.yoyCalcVal);
        },
        momMonthRateFmt() {
            return this.formatApiRate(this.summary?.mom?.rate);
        },
        yoyMonthRateFmt() {
            return this.formatApiRate(this.summary?.yoy?.rate);
        },
        momMonthRateCls() {
            return this.rateDeltaClass(this.summary?.mom?.rate);
        },
        yoyMonthRateCls() {
            return this.rateDeltaClass(this.summary?.yoy?.rate);
        },
        /* ── summary 파생 computed ── */
        /** 월간: 포커스 월 calcVal · 연간: 조회 연 기간 연평균 arithmeticMeanCalcVal */
        latestCalcValFmt() {
            if (this.isMonthPeriod) {
                const v = this.primaryMonthRow?.calcVal;
                if (v == null || !Number.isFinite(Number(v))) return "—";
                return Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 });
            }
            const v = this.summary?.arithmeticMeanCalcVal;
            if (v == null || !Number.isFinite(Number(v))) return "—";
            return Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 });
        },
        latestStatusCls() {
            return calcStatusBadge(this.primaryMonthRow?.calcStatus)?.cls ?? "muted";
        },
        latestStatusLabel() {
            const row = this.primaryMonthRow;
            if (!row) return "—";
            return calcStatusBadge(row.calcStatus, row.calcMsg).label;
        },
        meanCalcValFmt() {
            const v = this.summary?.arithmeticMeanCalcVal;
            if (v == null || !Number.isFinite(Number(v))) return "—";
            return Number(v).toLocaleString(undefined, { maximumFractionDigits: 4 });
        },
        meanCalcValNumber() {
            const v = Number(this.summary?.arithmeticMeanCalcVal);
            return Number.isFinite(v) ? v : null;
        },
        basePeriodLabel() {
            const p = this.summary?.period;
            if (p && p.fromYm != null && p.toYm != null) {
                return `${formatYm(p.fromYm)} ~ ${formatYm(p.toYm)}`;
            }
            return "—";
        },
        /** 연간 비교 카드: getMasterSummary.priorYearAvgs 직전·전전 연도 */
        momPeriodLabel() {
            const py = this.summary?.priorYearAvgs?.priorYear;
            if (py?.year != null && Number.isFinite(Number(py.year))) {
                return `${Number(py.year)}년`;
            }
            const y = yearFromDatePeriod(this.dateType, this.dateValue);
            if (y == null || y <= 2000) return "—";
            return `${y - 1}년`;
        },
        yoyPeriodLabel() {
            const ty = this.summary?.priorYearAvgs?.twoYearsPrior;
            if (ty?.year != null && Number.isFinite(Number(ty.year))) {
                return `${Number(ty.year)}년`;
            }
            const y = yearFromDatePeriod(this.dateType, this.dateValue);
            if (y == null || y <= 2001) return "—";
            return `${y - 2}년`;
        },
        momMeanCalcValFmt() {
            return this.formatCompareIntensityValue(this.summary?.priorYearAvgs?.priorYear?.arithmeticMeanCalcVal);
        },
        yoyMeanCalcValFmt() {
            return this.formatCompareIntensityValue(this.summary?.priorYearAvgs?.twoYearsPrior?.arithmeticMeanCalcVal);
        },
        momMeanDiffFmt() {
            return this.formatMeanDelta("mom");
        },
        yoyMeanDiffFmt() {
            return this.formatMeanDelta("yoy");
        },
        momMeanDiffCls() {
            return this.meanDeltaClass("mom");
        },
        yoyMeanDiffCls() {
            return this.meanDeltaClass("yoy");
        },
        trendFmt() {
            const r = Number(this.summary?.vsPeriodAvg?.rate);
            if (!Number.isFinite(r)) return "—";
            return `${r >= 0 ? "+" : ""}${r.toFixed(1)}%`;
        },
        trendDesc() {
            if (this.isMonthPeriod && this.summary?.vsPeriodAvg) {
                const c = Number(this.summary?.vsPeriodAvg?.currentVal);
                const p = Number(this.summary?.vsPeriodAvg?.periodAvg);
                if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return "";
                return c >= p ? "기간 평균 이상" : "기간 평균 이하";
            }
            const latest = Number(this.summary?.latestMonth?.calcVal);
            const mean = Number(this.summary?.arithmeticMeanCalcVal);
            if (!Number.isFinite(latest) || !Number.isFinite(mean) || mean === 0) return "";
            return latest >= mean ? "연 평균 이상" : "연 평균 이하";
        },
        trendCls() {
            if (this.isMonthPeriod && this.summary?.vsPeriodAvg) {
                const c = Number(this.summary.vsPeriodAvg?.currentVal);
                const p = Number(this.summary.vsPeriodAvg?.periodAvg);
                if (!Number.isFinite(c) || !Number.isFinite(p) || p === 0) return "";
                return c >= p ? "ei-bridge-stat-num--up" : "ei-bridge-stat-num--down";
            }
            const latest = Number(this.summary?.latestMonth?.calcVal);
            const mean = Number(this.summary?.arithmeticMeanCalcVal);
            if (!Number.isFinite(latest) || !Number.isFinite(mean) || mean === 0) return "";
            return latest >= mean ? "ei-bridge-stat-num--up" : "ei-bridge-stat-num--down";
        },
    },
    data() {
        return {
            eiDeltaColorCssVars: EI_DELTA_COLOR_CSS_VARS,
            loading: false,
            error: "",
            getMasterSummaryApiState: { response: null, data: null, loading: false, error: "" },
            barHoverMeta: null,
            /** 동일 calc 구간 재조회 중 차트용 캐시 (DataInput lastStableFetchMeta 와 동일 키) */
            lastStableFetchMeta: null,
            cachedBridgeMonths: null,
            /** 동일 요약 조회(enIntIdx|dateType|dateValue) 재호출 시 요약 본문 유지 */
            lastSummaryStableKey: null,
        };
    },
    watch: {
        bridgeLoadKey: {
            immediate: true,
            handler() {
                if (!this.enIntIdx) {
                    this.loading = false;
                    this.error = "";
                    this.getMasterSummaryApiState = { response: null, data: null, loading: false, error: "" };
                    this.barHoverMeta = null;
                    this.lastStableFetchMeta = null;
                    this.cachedBridgeMonths = null;
                    this.lastSummaryStableKey = null;
                    return;
                }
                this.load();
            },
        },
        sharedCalcListApiState: {
            deep: true,
            immediate: true,
            handler() {
                this.syncBridgeChartCacheFromShared();
            },
        },
    },
    methods: {
        formatYm,
        /** 비교 카드 등 원단위(calcVal) 표시 — 마스터 intensityUnit 붙임 */
        formatCompareIntensityValue(raw) {
            const v = Number(raw);
            if (!Number.isFinite(v)) return "—";
            const num = v.toLocaleString(undefined, { maximumFractionDigits: 4 });
            const u = String(this.master?.intensityUnit || "").trim();
            return u ? `${num} ${u}` : num;
        },
        formatApiRate(rate) {
            if (rate == null || !Number.isFinite(Number(rate))) return "—";
            const r = Number(rate);
            return `${r >= 0 ? "+" : ""}${r.toFixed(1)}%`;
        },
        rateDeltaClass(rate) {
            const r = Number(rate);
            if (!Number.isFinite(r)) return "";
            if (r > 0) return "ei-bridge-compare-diff--up";
            if (r < 0) return "ei-bridge-compare-diff--down";
            return "ei-bridge-compare-diff--flat";
        },
        formatMeanDelta(type) {
            const baseMean = this.meanCalcValNumber;
            const compareMean =
                type === "mom"
                    ? Number(this.summary?.priorYearAvgs?.priorYear?.arithmeticMeanCalcVal)
                    : Number(this.summary?.priorYearAvgs?.twoYearsPrior?.arithmeticMeanCalcVal);
            if (!Number.isFinite(baseMean) || !Number.isFinite(compareMean) || compareMean === 0) return "—";
            const pct = ((baseMean - compareMean) / compareMean) * 100;
            return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)}%`;
        },
        meanDeltaClass(type) {
            const baseMean = this.meanCalcValNumber;
            const compareMean =
                type === "mom"
                    ? Number(this.summary?.priorYearAvgs?.priorYear?.arithmeticMeanCalcVal)
                    : Number(this.summary?.priorYearAvgs?.twoYearsPrior?.arithmeticMeanCalcVal);
            if (!Number.isFinite(baseMean) || !Number.isFinite(compareMean)) return "";
            if (baseMean > compareMean) return "ei-bridge-compare-diff--up";
            if (baseMean < compareMean) return "ei-bridge-compare-diff--down";
            return "ei-bridge-compare-diff--flat";
        },
        /** 비교·요약 API: 200일 때만 본문 사용, 실패 시 null → 화면은 "—" */
        compareResponseData(res) {
            if (!res || res.status !== 200) return null;
            return res.data != null ? res.data : null;
        },
        /** 상위 calc 응답이 갱신될 때마다 차트용 월 배열 캐시 (성공 시만) */
        syncBridgeChartCacheFromShared() {
            const meta = this.bridgeFetchMetaKey;
            const s = this.sharedCalcListApiState;
            if (!this.enIntIdx || meta == null) {
                this.cachedBridgeMonths = null;
                this.lastStableFetchMeta = null;
                return;
            }
            if (!s || s.loading) return;
            if (s.error && !s.data) {
                this.cachedBridgeMonths = null;
                this.lastStableFetchMeta = null;
                return;
            }
            if (s.response?.status !== 200) return;
            const bounds = calcListBoundsFromDatePeriod(this.dateType, this.dateValue);
            if (!bounds) return;
            const allRows = Array.isArray(s.data?.rows) ? s.data.rows : [];
            const baseRows = allRows.filter((r) => {
                const ym = Number(r.regYm);
                return Number.isFinite(ym) && ym >= bounds.fromYm && ym <= bounds.toYm;
            });
            this.cachedBridgeMonths = bridgeChartMonthsFromCalcRows(baseRows);
            this.lastStableFetchMeta = meta;
        },
        async load() {
            this.barHoverMeta = null;
            if (!this.enIntIdx || !isValidDatePeriod(this.dateType, this.dateValue)) {
                this.loading = false;
                this.error = "";
                this.getMasterSummaryApiState = { response: null, data: null, loading: false, error: "" };
                this.lastSummaryStableKey = null;
                return;
            }

            const summaryKey = `${this.enIntIdx}|${this.dateType}|${this.dateValue}`;
            const prevSummary = this.getMasterSummaryApiState;
            const preserveSummary = Boolean(prevSummary?.data) && this.lastSummaryStableKey === summaryKey;

            this.loading = true;
            this.error = "";
            if (preserveSummary) {
                this.getMasterSummaryApiState = { ...prevSummary, loading: true, error: "" };
            } else {
                this.getMasterSummaryApiState = { response: null, data: null, loading: true, error: "" };
            }
            try {
                const summaryParams = {
                    enIntIdx: this.enIntIdx,
                    dateType: this.dateType,
                    dateValue: this.dateValue,
                };
                const getMasterSummary_response = await backEndApi.enInt.getMasterSummary(summaryParams);
                const getMasterSummary_data = this.compareResponseData(getMasterSummary_response);
                this.getMasterSummaryApiState = {
                    response: getMasterSummary_response,
                    data: getMasterSummary_data,
                    loading: false,
                    error: getMasterSummary_data ? "" : "요약 데이터를 불러오지 못했습니다.",
                };
                this.lastSummaryStableKey = getMasterSummary_data ? summaryKey : null;
            } catch {
                this.error = "요약을 불러오지 못했습니다.";
                this.getMasterSummaryApiState = {
                    response: null,
                    data: null,
                    loading: false,
                    error: "요약 데이터를 불러오지 못했습니다.",
                };
                this.lastSummaryStableKey = null;
            } finally {
                this.loading = false;
            }
        },
        onBarHover(payload) {
            if (!payload || !Number.isFinite(payload.value)) {
                this.barHoverMeta = null;
                return;
            }
            this.barHoverMeta = {
                value: payload.value,
                category: payload.category != null ? String(payload.category) : "",
            };
        },
        onBarHoverEnd() {
            this.barHoverMeta = null;
        },
    },
};
</script>

<style scoped>
.ei-bridge {
    --ei-bridge-font-base: 12px;
    --ei-bridge-font-title: 11px;
    --ei-bridge-font-subtle: 10px;
    --ei-bridge-font-weight-title: 700;
    --ei-bridge-color-title: #64748b;
    --ei-bridge-color-subtle: #94a3b8;
    width: 100%;
    min-width: 0;
    flex-shrink: 0;
    display: flex;
    flex-direction: column;
    font-size: var(--ei-bridge-font-base);
    line-height: 1.35;
    min-height: 0;
    align-self: stretch;
    border-right: 1px solid rgba(203, 213, 225, 0.84);
    background:
        linear-gradient(180deg, rgba(255, 255, 255, 0.98), rgba(248, 250, 252, 0.96)),
        radial-gradient(circle at top, rgba(14, 116, 144, 0.06), transparent 44%);
    box-shadow: inset -1px 0 0 rgba(255, 255, 255, 0.62);
}

.ei-bridge button,
.ei-bridge input,
.ei-bridge select {
    font-size: inherit;
}

.ei-bridge-primary-label,
.ei-bridge-stat-label,
.ei-bridge-compare-title,
.ei-bridge-quality-label,
.ei-bridge-section-title {
    font-size: var(--ei-bridge-font-title);
    color: var(--ei-bridge-color-title);
}

.ei-bridge-scroll {
    flex: 1;
    min-height: 0;
    min-width: 0;
    overflow-y: auto;
    overflow-x: hidden;
    scrollbar-gutter: stable;
    display: flex;
    flex-direction: column;
}

/* 상단 블록은 내용 높이 유지, 차트(막대+분포) 패널만 남는 세로 공간 확장 */
.ei-bridge-scroll > :not(.ei-bridge-charts-panel) {
    flex-shrink: 0;
}

.ei-bridge-section--boxplot {
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.ei-bridge-boxplot-grow {
    flex: 1 1 auto;
    min-height: 120px;
    display: flex;
    flex-direction: column;
    min-width: 0;
}

.ei-bridge-section--boxplot > .ei-bridge-section-title {
    flex-shrink: 0;
}

.ei-bridge-empty {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
    padding: 12px 10px;
    text-align: center;
    font-weight: 600;
    color: #94a3b8;
    line-height: 1.45;
}

.ei-bridge-empty-icon {
    font-size: 18px;
    opacity: 0.65;
}

.ei-bridge-loading {
    padding: 12px;
    text-align: center;
    color: #64748b;
}

.ei-bridge-error {
    padding: 10px 12px;
    margin: 8px;
    border-radius: 6px;
    font-weight: 600;
    color: #991b1b;
    background: rgba(220, 38, 38, 0.08);
}

.ei-bridge-head {
    flex-shrink: 0;
    padding: 10px 12px 9px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.95);
}

.ei-bridge-identity {
    display: flex;
    gap: 8px;
    align-items: flex-start;
}

.ei-bridge-icon-wrap {
    flex-shrink: 0;
    width: 32px;
    height: 32px;
    border-radius: 8px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: linear-gradient(180deg, rgba(14, 116, 144, 0.14), rgba(14, 116, 144, 0.06)), rgba(255, 255, 255, 0.72);
    color: #0e7490;
    font-size: 15px;
    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.7);
}

.ei-bridge-titles {
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 3px;
}

.ei-bridge-code {
    font-size: var(--ei-bridge-font-title);
    font-weight: 700;
    color: var(--ei-bridge-color-title);
    line-height: 1.2;
}

.ei-bridge-name {
    font-size: var(--ei-bridge-font-title);
    font-weight: 700;
    color: #0f172a;
    line-height: 1.35;
    word-break: break-word;
}

.ei-bridge-cat {
    font-size: var(--ei-bridge-font-title);
    font-weight: 600;
    color: #0e7490;
    line-height: 1.2;
}

.ei-bridge-unit-line {
    margin-top: 10px;
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 8px;
    padding: 6px 8px;
    border-radius: 6px;
    background: rgba(241, 245, 249, 0.85);
    border: 1px solid rgba(226, 232, 240, 0.9);
}

.ei-bridge-unit-key {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 700;
    color: var(--ei-bridge-color-title);
}

.ei-bridge-unit-val {
    font-size: var(--ei-bridge-font-title);
    font-weight: 700;
    color: #334155;
    text-align: right;
}

/* ── 1차: 최근 원단위 ── */
.ei-bridge-primary {
    padding: 8px 12px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.95);
    background: linear-gradient(180deg, rgba(14, 116, 144, 0.04), rgba(255, 255, 255, 0));
}

.ei-bridge-primary-meta {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 6px;
    margin-bottom: 4px;
}

.ei-bridge-primary-label {
    font-weight: 700;
}

.ei-bridge-status-chip {
    display: inline-flex;
    align-items: center;
    padding: 1px 6px;
    border-radius: 999px;
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 700;
    line-height: 1.25;
}

.ei-bridge-status-chip.ok {
    background: rgba(16, 185, 129, 0.12);
    color: #047857;
}

.ei-bridge-status-chip.warn {
    background: rgba(245, 158, 11, 0.12);
    color: #b45309;
}

.ei-bridge-status-chip.danger {
    background: rgba(239, 68, 68, 0.1);
    color: #b91c1c;
}

.ei-bridge-status-chip.muted {
    background: rgba(148, 163, 184, 0.12);
    color: #475569;
}

.ei-bridge-primary-val-row {
    display: flex;
    align-items: baseline;
    gap: 4px;
}

.ei-bridge-primary-num {
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.05em;
    line-height: 1.1;
}

.ei-bridge-primary-unit {
    font-size: var(--ei-bridge-font-title);
    font-weight: 600;
    color: var(--ei-bridge-color-title);
}

.ei-bridge-primary-month {
    margin-top: 3px;
    font-size: var(--ei-bridge-font-subtle);
    color: var(--ei-bridge-color-subtle);
    font-weight: 600;
    line-height: 1.2;
}

/* ── 2차: 기간 평균 + 트렌드 ── */
.ei-bridge-stats-row {
    display: flex;
    align-items: stretch;
    gap: 0;
    padding: 8px 12px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.95);
}

.ei-bridge-stat {
    flex: 1;
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
}

.ei-bridge-stat-sep {
    width: 1px;
    background: rgba(226, 232, 240, 0.9);
    margin: 0 8px;
    flex-shrink: 0;
}

.ei-bridge-stat-label {
    font-weight: 700;
    line-height: 1.2;
}

.ei-bridge-stat-num {
    font-size: inherit;
    font-weight: 700;
    color: #1e293b;
}

.ei-bridge-stat-num--up {
    color: var(--ei-delta-up);
}

.ei-bridge-stat-num--down {
    color: var(--ei-delta-down);
}

.ei-bridge-stat-sub {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 600;
    color: var(--ei-bridge-color-subtle);
    line-height: 1.2;
}

.ei-bridge-compare {
    padding: 8px 12px 9px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.95);
}

.ei-bridge-compare-head {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 6px;
    margin-bottom: 5px;
}

.ei-bridge-compare-title {
    font-weight: 700;
    line-height: 1.2;
}

.ei-bridge-compare-base {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 600;
    color: var(--ei-bridge-color-subtle);
    font-variant-numeric: tabular-nums;
    text-align: right;
}

.ei-bridge-compare-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 6px;
}

.ei-bridge-compare-card {
    padding: 6px 7px;
    border-radius: 7px;
    border: 1px solid rgba(226, 232, 240, 0.92);
    background: rgba(255, 255, 255, 0.82);
    box-shadow: 0 2px 8px rgba(148, 163, 184, 0.06);
}

.ei-bridge-compare-card-head {
    display: flex;
    flex-direction: column;
    gap: 1px;
    margin-bottom: 3px;
}

.ei-bridge-compare-card-label {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 700;
    color: var(--ei-bridge-color-title);
    line-height: 1.2;
}

.ei-bridge-compare-card-range {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 600;
    color: var(--ei-bridge-color-subtle);
    line-height: 1.2;
    font-variant-numeric: tabular-nums;
}

.ei-bridge-compare-card-body {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    justify-content: space-between;
    gap: 6px;
}

.ei-bridge-compare-card-val {
    min-width: 0;
    font-size: inherit;
    font-weight: 800;
    color: #1e293b;
    line-height: 1.3;
}

.ei-bridge-compare-diff {
    font-size: inherit;
    font-weight: 700;
    font-variant-numeric: tabular-nums;
    color: #64748b;
}

.ei-bridge-compare-diff--up {
    color: var(--ei-delta-up);
}

.ei-bridge-compare-diff--down {
    color: var(--ei-delta-down);
}

.ei-bridge-compare-diff--flat {
    color: #64748b;
}

/* ── 3차: 정상 비율 ── */
.ei-bridge-quality {
    padding: 6px 12px 7px;
    border-bottom: 1px solid rgba(241, 245, 249, 0.95);
}

.ei-bridge-quality-head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 3px;
}

.ei-bridge-quality-label {
    font-weight: 700;
}

.ei-bridge-quality-ratio {
    font-size: inherit;
    font-weight: 800;
    color: #334155;
    font-variant-numeric: tabular-nums;
}

.ei-bridge-quality-den {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 600;
    color: var(--ei-bridge-color-subtle);
}

.ei-bridge-quality-track {
    height: 4px;
    border-radius: 999px;
    background: rgba(226, 232, 240, 0.8);
}

.ei-bridge-quality-fill {
    height: 100%;
    border-radius: 999px;
    transition: width 0.4s ease;
}

.ei-bridge-quality-fill--ok {
    background: #10b981;
}

.ei-bridge-quality-fill--warn {
    background: #f59e0b;
}

.ei-bridge-quality-fill--danger {
    background: #ef4444;
}

.ei-bridge-quality-warn {
    margin-top: 4px;
    font-size: var(--ei-bridge-font-title);
    font-weight: 700;
    color: #b45309;
    display: flex;
    align-items: center;
    gap: 4px;
}

/* 막대·박스 차트 묶음: 스크롤 영역 하단 확장 + 최소 높이(로딩 오버레이 시 밀림 완화) */
.ei-bridge-charts-panel {
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1 1 auto;
    min-height: 240px;
}

.ei-bridge-charts-panel-body {
    position: relative;
    z-index: 0;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
}

.ei-bridge-charts-panel-overlay {
    position: absolute;
    inset: 0;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(248, 250, 252, 0.9);
    border-radius: 8px;
    pointer-events: none;
}

.ei-bridge-charts-panel-spinner {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    font-size: 12px;
    font-weight: 600;
    color: #64748b;
}

.ei-bridge-charts-panel-body--blocked {
    pointer-events: none;
    user-select: none;
}

.ei-bridge-section {
    padding: 8px 10px 9px;
    min-height: 0;
}

.ei-bridge-section.ei-bridge-section--boxplot {
    padding-bottom: 10px;
}

.ei-bridge-section-title {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
    font-weight: 700;
    margin-bottom: 5px;
    padding-left: 0;
}

.ei-bridge-chart-legend {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: flex-end;
    gap: 2px 6px;
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 600;
    text-align: right;
}

.ei-bridge-legend-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
}

.ei-bridge-legend-sep {
    color: #cbd5e1;
    font-weight: 500;
    user-select: none;
}

/* EnIntBridgeBarChart 막대·라인과 동일 톤 */
.ei-bridge-legend-swatch--bar {
    width: 10px;
    height: 12px;
    border-radius: 2px 2px 0 0;
    background: #cbd5e1;
    flex-shrink: 0;
}

.ei-bridge-legend-swatch--mom {
    width: 20px;
    height: 0;
    flex-shrink: 0;
    border: none;
    border-top: 2px dashed #475569;
    margin-top: 1px;
}

.ei-bridge-legend-swatch--yoy {
    width: 20px;
    height: 0;
    flex-shrink: 0;
    border: none;
    border-top: 2px dotted #94a3b8;
    margin-top: 1px;
}

.ei-bridge-legend-swatch--avg {
    width: 20px;
    height: 0;
    flex-shrink: 0;
    border: none;
    border-top: 2px solid #0f766e;
    opacity: 0.8;
}

.ei-bridge-legend-text--bar {
    color: #64748b;
}

.ei-bridge-legend-text--mom {
    color: #475569;
}

.ei-bridge-legend-text--yoy {
    color: #94a3b8;
}

.ei-bridge-legend-text--avg {
    color: #0f766e;
}

.ei-bridge-actions {
    flex-shrink: 0;
    padding: 8px 12px 10px;
    border-top: 1px solid rgba(241, 245, 249, 0.95);
}

.ei-bridge-meta-dates {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 8px;
    margin-top: 8px;
    border-top: 1px solid rgba(241, 245, 249, 0.9);
}

.ei-bridge-meta-col {
    display: flex;
    gap: 2px;
    min-width: 0;
}

.ei-bridge-meta-col--end {
    text-align: right;
    align-items: flex-end;
}

.ei-bridge-meta-k {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 700;
    color: var(--ei-bridge-color-subtle);
    line-height: 1.2;
}

.ei-bridge-meta-v {
    font-size: var(--ei-bridge-font-subtle);
    font-weight: 600;
    color: var(--ei-bridge-color-title);
    font-variant-numeric: tabular-nums;
    line-height: 1.25;
    word-break: break-all;
}

.ei-bridge-edit-btn {
    width: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    padding: 7px 10px;
    border-radius: 8px;
    border: 1px solid rgba(14, 116, 144, 0.35);
    background: #0e7490;
    color: #fff;
    font-size: inherit;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.15s;
}

.ei-bridge-edit-btn:hover {
    background: #0c6a82;
}

@media (max-width: 1320px) {
    .ei-bridge-section-title {
        flex-direction: column;
    }

    .ei-bridge-chart-legend {
        justify-content: flex-start;
    }
}

@media (max-width: 960px) {
    .ei-bridge {
        border-right: none;
        border-bottom: 1px solid rgba(203, 213, 225, 0.84);
    }
}
</style>

<!-- ECharts tooltip은 appendToBody로 document에 붙음 — scoped 밖에서 스타일 적용 -->
<style>
.ei-bridge-echarts-tooltip {
    --ei-bridge-tip-font-title: 11px;
    --ei-bridge-tip-font-subtle: 10px;
    pointer-events: none;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-card {
    pointer-events: none;
    padding: 10px 12px 11px;
    border-radius: 10px;
    background: rgba(255, 255, 255, 0.98);
    border: 1px solid rgba(226, 232, 240, 0.95);
    border-left: 3px solid #0e7490;
    box-shadow:
        0 10px 40px rgba(15, 23, 42, 0.1),
        0 2px 8px rgba(15, 23, 42, 0.06),
        0 0 0 1px rgba(15, 23, 42, 0.04);
    backdrop-filter: blur(10px);
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-heading {
    font-size: var(--ei-bridge-tip-font-subtle);
    font-weight: 700;
    color: #64748b;
    letter-spacing: -0.02em;
    line-height: 1.35;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-primary {
    margin-top: 5px;
    font-size: 13px;
    font-weight: 800;
    color: #0f172a;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.03em;
    line-height: 1.3;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-muted {
    margin-top: 5px;
    font-size: var(--ei-bridge-tip-font-title);
    font-weight: 600;
    color: #94a3b8;
    line-height: 1.35;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-rows {
    margin-top: 8px;
    padding-top: 8px;
    border-top: 1px solid rgba(241, 245, 249, 0.95);
    display: flex;
    flex-direction: column;
    gap: 5px;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-kv {
    display: flex;
    align-items: baseline;
    justify-content: space-between;
    gap: 14px;
    line-height: 1.3;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-k {
    flex-shrink: 0;
    font-size: var(--ei-bridge-tip-font-subtle);
    font-weight: 700;
    color: #94a3b8;
}

.ei-bridge-echarts-tooltip .ei-bridge-tooltip-v {
    text-align: right;
    font-size: var(--ei-bridge-tip-font-title);
    font-weight: 700;
    color: #1e293b;
    font-variant-numeric: tabular-nums;
    letter-spacing: -0.02em;
}
</style>
